import React, { FC, useEffect, useRef, useState } from 'react'
import { DotLottieReact } from '@lottiefiles/dotlottie-react'
import type { DotLottie, DotLottieReactProps } from '@lottiefiles/dotlottie-react'

/**
 * @interface LottieProps
 * @description Props for the Lottie animation wrapper component.
 * @property {string} [src] - Path or URL to the .lottie or .json animation file.
 * @property {boolean} [loop] - Whether the animation should loop. Defaults to true.
 * @property {boolean} [autoplay] - Whether the animation should autoplay. Defaults to true.
 * @property {string} [className] - Additional class names for the wrapper div.
 */
interface LottieProps extends Omit<DotLottieReactProps, 'src'> {
	src?: string
	loop?: boolean
	autoplay?: boolean
	className?: string
}

/**
 * @component Lottie
 * @description Wrapper around DotLottieReact for rendering .lottie and .json animations.
 * Automatically resizes the canvas when the wrapper dimensions change via ResizeObserver.
 */
const Lottie: FC<LottieProps> = ({ src, loop = true, autoplay = true, className, ...rest }) => {
	const wrapperRef = useRef<HTMLDivElement>(null)
	const [dotLottie, setDotLottie] = useState<DotLottie | null>(null)
	const [visible, setVisible] = useState(false)

	// Defer fetching the animation JSON (often >1MB) + dotLottie WASM until the
	// wrapper nears the viewport. Keeps these heavy assets out of the initial load.
	useEffect(() => {
		const el = wrapperRef.current
		if (!el || visible) return

		let io: IntersectionObserver | null = null
		const start = () => {
			if (visible || !wrapperRef.current) return
			io = new IntersectionObserver(
				(entries) => {
					if (entries.some(e => e.isIntersecting)) {
						setVisible(true)
						io?.disconnect()
					}
				},
				{ rootMargin: '100px' }
			)
			io.observe(wrapperRef.current)
		}

		// Defer the observer past the LCP measurement window so the (often heavy)
		// Lottie JSON + dotLottie WASM don't compete with the LCP element for
		// bandwidth or main-thread time on initial load.
		const handle = window.setTimeout(start, 1500)

		return () => {
			io?.disconnect()
			window.clearTimeout(handle)
		}
	}, [visible])

	useEffect(() => {
		if (!dotLottie || !wrapperRef.current) return

		const observer = new ResizeObserver(() => {
			dotLottie.resize()
		})

		observer.observe(wrapperRef.current)

		return () => observer.disconnect()
	}, [dotLottie])

	return (
		<div ref={ wrapperRef } className={ className }>
			{ visible && (
				<DotLottieReact
					src={ src }
					loop={ loop }
					autoplay={ autoplay }
					dotLottieRefCallback={ setDotLottie }
					style={{ width: '100%', height: '100%' }}
					{ ...rest }
				/>
			) }
		</div>
	)
}

export default Lottie
